/**
 * A registration is a partner's existing client put forward to Wally (convention art. 2); for Wally
 * it is a lead. Its decision status and, once accepted, its sales pipeline status (from the HubSpot
 * deal) are separate: see docs/PLAN.md §6.
 */
import { z } from 'zod';
import { isoDay, objectIdSchema } from './common.js';
import { clientNumberInputSchema } from './company-number.js';
export const registrationStatusSchema = z.enum([
    'submitted',
    'evidence_requested',
    'accepted',
    'refused',
    'withdrawn',
]);
export const pipelineStageSchema = z.enum([
    'in_progress',
    'demo_planned',
    'demo_done',
    'customer',
    'lost',
]);
/** HubSpot `dealstage` values (Sales Pipeline) to the portal's pipeline stage. */
export const HUBSPOT_STAGE_MAP = {
    appointmentscheduled: 'in_progress',
    qualifiedtobuy: 'in_progress',
    presentationscheduled: 'demo_planned',
    decisionmakerboughtin: 'demo_done',
    closedwon: 'customer',
    closedlost: 'lost',
};
/** A deal in the Customer success pipeline is a customer, whatever its stage. */
export const HUBSPOT_CUSTOMER_SUCCESS_PIPELINE = '2693880041';
export function pipelineStageFor(pipeline, dealstage) {
    if (pipeline === HUBSPOT_CUSTOMER_SUCCESS_PIPELINE)
        return 'customer';
    return HUBSPOT_STAGE_MAP[dealstage] ?? null;
}
/**
 * A partner that sells itself (`salesByPartner`) reports where an accepted lead stands; the
 * contract date comes with "customer" (today unless given, never in the future).
 */
export const reportStageSchema = z
    .object({
    stage: pipelineStageSchema,
    contractSignedOn: isoDay.nullable().optional(),
})
    .strict()
    .refine((v) => v.stage === 'customer' || !v.contractSignedOn, {
    message: 'A contract date only goes with "customer"',
    path: ['contractSignedOn'],
});
export function portalClientStatus(status, stage) {
    return status === 'accepted' ? (stage ?? 'in_progress') : status;
}
// Input
const text = (max) => z.string().trim().max(max);
/** What a partner tells about the client relationship (art. 2.1: effective and demonstrable). */
export const relationshipSchema = z.object({
    /** Since when the office is the partner's client, free text ("2019", "mars 2021"). */
    since: text(60).min(1),
    /** What the partner delivers to the office (software, services), enough for Wally to check it. */
    description: text(2000).min(10),
});
export const contactSchema = z
    .object({
    name: text(120),
    email: z.union([z.literal(''), z.string().trim().toLowerCase().email().max(254)]),
    phone: text(40),
})
    .partial();
export const createRegistrationSchema = z
    .object({
    legalName: text(200).min(2),
    /** `{ country, number, rcs }`; parsed to `{ country, companyNumber, rcsNumber }`. */
    companyNumber: clientNumberInputSchema,
    relationship: relationshipSchema,
    contact: contactSchema.default({}),
    /**
     * The entity the person chose in the form. Never the source of the partner (the session is):
     * the request is refused when the session is on another entity (another tab switched it).
     */
    forPartnerId: objectIdSchema.optional(),
})
    .strict();
/** A partner corrects a registration Wally has not decided on yet (art. 2.2.1); the number stays. */
export const updateRegistrationSchema = z
    .object({
    legalName: text(200).min(2).optional(),
    relationship: relationshipSchema.optional(),
    contact: contactSchema.optional(),
})
    .strict();
export const answerEvidenceSchema = z.object({ answer: text(4000).min(2) }).strict();
/**
 * Wally's decision (art. 2.3, 2.4). An evidence request is a message for the partner. A refusal
 * always records its reason, but the partner only sees it when Wally shares it: art. 2.4 says the
 * reason is explained on request.
 */
export const decideRegistrationSchema = z.discriminatedUnion('decision', [
    z.object({ decision: z.literal('accept') }).strict(),
    z
        .object({
        decision: z.literal('refuse'),
        reason: text(2000).min(5),
        shareReason: z.boolean().default(false),
    })
        .strict(),
    z.object({ decision: z.literal('request_evidence'), message: text(2000).min(5) }).strict(),
]);
/** Staff corrections of the automatic links and dates (plan §6.3). null clears an override. */
export const registrationLinksSchema = z
    .object({
    hubspotCompanyId: z.string().regex(/^\d+$/).nullable().optional(),
    hubspotDealId: z.string().regex(/^\d+$/).nullable().optional(),
    contractSignedOn: isoDay.nullable().optional(),
    /** The Wally organisation of the office, when HubSpot's `wally_org_id` is missing or wrong. */
    wallyOrganizationId: objectIdSchema.nullable().optional(),
    /** The Stripe customer that pays for the office (for a billing group: the parent's). */
    stripeCustomerId: z
        .string()
        .regex(/^cus_[A-Za-z0-9]+$/)
        .nullable()
        .optional(),
})
    .strict();
/** Names compared the way people type them: surrounding and repeated spaces do not count. */
export function sameName(name) {
    return name.trim().replace(/\s+/g, ' ');
}
/**
 * Staff remove a lead for good. `confirmName` must be the lead's legal name as stored, typed by the
 * person deleting: a deletion is never a slip of the mouse.
 */
export const deleteRegistrationSchema = z
    .object({
    // Compared, and limited, the way the dialog compares it.
    confirmName: z.string().max(1000).transform(sameName).pipe(z.string().min(1).max(200)),
    reason: z.string().trim().max(1000).default(''),
})
    .strict();
/** One entry of the deletion log (an append-only audit event). */
export const listDeletionsQuerySchema = z.object({
    /** `nextCursor` of the previous page (`<deletedAt>|<entry id>`): the page continues after it. */
    before: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}T[\d:.]+Z\|[a-f\d]{24}$/i)
        .optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const listRegistrationsQuerySchema = z.object({
    status: registrationStatusSchema.optional(),
    partnerId: objectIdSchema.optional(),
});
